import { Injectable, signal } from '@angular/core';
import { Filter, Presets } from '../models/filter-models';
import { FilterChange, PendingPresetChange } from '../models/preset-change.models';
import { FilterService } from './filter.service';
import { PresetsService } from './presets.service';
import { SettingsService } from './settings.service';
import {
  collectFilterDiffs,
  collectUserModificationDiffKeys,
  formatPropertyLabel,
  getSectionForKey,
} from './filter-diff.util';

interface PresetChangeOptions {
  forcePreview?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PresetPreviewService {
  pendingChange = signal<PendingPresetChange | null>(null);

  constructor(
    private filterService: FilterService,
    private presetsService: PresetsService,
    private settingsService: SettingsService,
  ) {}

  previewProgressPresetChange(newPreset: Presets): void {
    this.initiateProgressPresetChange(newPreset, { forcePreview: true });
  }

  previewClassPresetChange(newPreset: string): void {
    this.initiateClassPresetChange(newPreset, { forcePreview: true });
  }

  initiateProgressPresetChange(newPreset: Presets, options?: PresetChangeOptions): void {
    const activeFilter = this.filterService.activeFilter().filter;
    const currentPreset = activeFilter.progressPreset;
    if (!options?.forcePreview && !this.settingsService.autoPreviewPresetChanges()) {
      this.presetsService.setProgressPreset(newPreset);
      return;
    }

    const userModifiedKeys = this.getUserModifiedKeys(activeFilter, currentPreset, 'progress');

    if (!options?.forcePreview && userModifiedKeys.size === 0) {
      this.presetsService.setProgressPreset(newPreset);
      return;
    }

    const originalFilter = structuredClone(activeFilter) as Filter;
    const targetFilter = structuredClone(originalFilter) as Filter;
    this.presetsService.setProgressPreset(newPreset, { filter: targetFilter, dryRun: true });
    const changes = this.buildChanges(originalFilter, targetFilter, userModifiedKeys);

    if (!options?.forcePreview && changes.length === 0) {
      this.presetsService.setProgressPreset(newPreset);
      return;
    }

    this.setPendingChange('progress', currentPreset, newPreset, changes, targetFilter, originalFilter, !options?.forcePreview);
  }

  initiateClassPresetChange(newPreset: string, options?: PresetChangeOptions): void {
    const activeFilter = this.filterService.activeFilter().filter;
    const currentPreset = activeFilter.classPreset;

    if (!options?.forcePreview && !currentPreset) {
      this.presetsService.setClassPreset(newPreset);
      return;
    }

    if (!options?.forcePreview && !this.settingsService.autoPreviewPresetChanges()) {
      this.presetsService.setClassPreset(newPreset);
      return;
    }

    const userModifiedKeys = this.getUserModifiedKeys(activeFilter, currentPreset, 'class');

    if (!options?.forcePreview && userModifiedKeys.size === 0) {
      this.presetsService.setClassPreset(newPreset);
      return;
    }

    const originalFilter = structuredClone(activeFilter) as Filter;
    const targetFilter = structuredClone(originalFilter) as Filter;
    this.presetsService.setClassPreset(newPreset, { filter: targetFilter, dryRun: true });
    const changes = this.buildChanges(originalFilter, targetFilter, userModifiedKeys);

    if (!options?.forcePreview && changes.length === 0) {
      this.presetsService.setClassPreset(newPreset);
      return;
    }

    this.setPendingChange('class', currentPreset, newPreset, changes, targetFilter, originalFilter, !options?.forcePreview);
  }

  updatePreviewPreset(preset: string): void {
    const pending = this.pendingChange();
    if (!pending) return;

    const proposedFilter = structuredClone(pending.originalFilter) as Filter;
    if (pending.type === 'progress') {
      this.presetsService.setProgressPreset(preset as Presets, { filter: proposedFilter, dryRun: true });
    } else {
      this.presetsService.setClassPreset(preset, { filter: proposedFilter, dryRun: true });
    }

    const userModifiedKeys = this.getUserModifiedKeys(
      pending.originalFilter,
      pending.originalPreset,
      pending.type,
    );
    const changes = this.buildChanges(pending.originalFilter, proposedFilter, userModifiedKeys);

    this.pendingChange.set({
      ...pending,
      newPreset: preset,
      targetFilter: proposedFilter,
      changes,
    });
  }

  confirmChange(): void {
    const pending = this.pendingChange();
    if (!pending) return;

    const actualFilter = this.filterService.activeFilter().filter;
    Object.assign(actualFilter, structuredClone(pending.targetFilter));

    this.filterService.updateAllTiers();
    this.filterService.updateFilter();
    this.pendingChange.set(null);
  }

  cancelChange(): void {
    this.pendingChange.set(null);
  }

  private setPendingChange(
    type: 'progress' | 'class',
    originalPreset: string | null,
    newPreset: string,
    changes: FilterChange[],
    targetFilter: Filter,
    originalFilter: Filter,
    openedAutomatically: boolean,
  ): void {
    this.pendingChange.set({
      type,
      originalPreset,
      newPreset,
      changes,
      targetFilter,
      originalFilter,
      openedAutomatically,
    });
  }

  private buildBaselineFilter(activeFilter: Filter, currentPreset: string | null, type: 'progress' | 'class'): Filter {
    if (!currentPreset) {
      return this.filterService.createNewFilter();
    }

    const baselineFilter = structuredClone(activeFilter) as Filter;
    if (type === 'progress') {
      this.presetsService.setProgressPreset(currentPreset as Presets, { filter: baselineFilter, dryRun: true });
    } else {
      this.presetsService.setClassPreset(currentPreset, { filter: baselineFilter, dryRun: true });
    }
    return baselineFilter;
  }

  private getUserModifiedKeys(activeFilter: Filter, currentPreset: string | null, type: 'progress' | 'class'): Set<string> {
    const baselineFilter = this.buildBaselineFilter(activeFilter, currentPreset, type);
    return collectUserModificationDiffKeys(activeFilter, baselineFilter);
  }

  private buildChanges(
    originalFilter: Filter,
    targetFilter: Filter,
    userModifiedKeys: Set<string>,
  ): FilterChange[] {
    return collectFilterDiffs(originalFilter, targetFilter).map(diff => ({
      key: diff.key,
      section: getSectionForKey(diff.key),
      label: diff.label ?? formatPropertyLabel(diff.key),
      currentValue: diff.currentValue,
      newValue: diff.newValue,
      isUserModified: userModifiedKeys.has(diff.key),
      groupLabel: diff.groupLabel,
    }));
  }
}
